import { createHttpClient } from '../../../api/http';
import { serviceUrls } from '../../../config/serviceUrls';

export const billingClient = createHttpClient(serviceUrls.gateway);

/** Billing plan from GET /v1/billing/plans */
export interface BillingPlan {
  plan_id: string;
  scope?: string;
  name: string;
  interval: 'month' | 'year';
  amount: number;
  currency: string;
  status: string;
  stripe_product_id?: string;
  stripe_price_id?: string;
  features?: Record<string, boolean>;
  limits?: Record<string, number>;
  description?: string;
  marketing_features?: string[];
  attributes?: Record<string, unknown>;
  statement_descriptor?: string;
  metadata?: Record<string, unknown>;
  prices_history?: unknown[];
  created_at?: string;
  updated_at?: string;
}

/** Request body for POST /v1/billing/plans */
export interface CreatePlanRequest {
  name: string;
  interval: 'month' | 'year';
  amount: number;
  currency: string;
  description?: string;
  marketing_features?: string[];
  attributes?: Record<string, unknown>;
  statement_descriptor?: string;
  metadata?: Record<string, unknown>;
  features?: Record<string, boolean>;
  limits?: Record<string, number>;
}

export interface ListPlansParams {
  limit?: number;
  skip?: number;
}

/** GET /v1/billing/plans */
export const listPlans = async (params?: ListPlansParams): Promise<BillingPlan[]> => {
  const response = await billingClient.get<BillingPlan[]>('/v1/billing/plans', {
    params: { limit: params?.limit ?? 100, skip: params?.skip },
  });
  const data = response.data;
  return Array.isArray(data) ? data : [];
};

/** POST /v1/billing/plans */
export const createPlan = async (data: CreatePlanRequest): Promise<BillingPlan> => {
  const response = await billingClient.post<BillingPlan>('/v1/billing/plans', data);
  return response.data;
};

/** POST /v1/billing/plans/:planId/activate */
export const activatePlan = async (planId: string): Promise<BillingPlan> => {
  const response = await billingClient.post<BillingPlan>(`/v1/billing/plans/${planId}/activate`);
  return response.data;
};

/** POST /v1/billing/plans/:planId/deactivate */
export const deactivatePlan = async (planId: string): Promise<BillingPlan> => {
  const response = await billingClient.post<BillingPlan>(`/v1/billing/plans/${planId}/deactivate`);
  return response.data;
};

/** GET /v1/billing/plans/public — list plans available for a tenant (e.g. school admin). Send X-Tenant-Id. */
export const getPublicPlans = async (tenantId: string): Promise<BillingPlan[]> => {
  const response = await billingClient.get<BillingPlan[]>('/v1/billing/plans/public', {
    headers: { 'X-Tenant-Id': tenantId },
  });
  const data = response.data;
  return Array.isArray(data) ? data : [];
};

/** Tenant subscription from GET /v1/billing/tenant-subscription/me */
export type TenantSubscription = {
  tenant_id: string;
  plan_id: string;
  status: 'active' | 'trialing' | 'canceled' | 'past_due' | 'incomplete' | string;
  current_period_start: string;
  current_period_end: string;
  entitlements_snapshot?: {
    plan_name?: string;
    interval?: 'month' | 'year' | string;
    amount?: number;
    currency?: string;
    features?: Record<string, unknown>;
    limits?: Record<string, unknown>;
  };
  has_premium_access?: boolean;
  grace_until?: string | null;
};

type ApiErrorEnvelope = { error?: { code?: string; message?: string } };

/** GET /v1/billing/tenant-subscription/me — returns null when NOT_FOUND (no active subscription). */
export async function getMyTenantSubscription(tenantId: string): Promise<TenantSubscription | null> {
  try {
    const res = await billingClient.get<TenantSubscription>('/v1/billing/tenant-subscription/me', {
      headers: { 'X-Tenant-Id': tenantId },
    });
    return res.data;
  } catch (e: unknown) {
    const data: ApiErrorEnvelope | undefined =
      (e as { response?: { data?: ApiErrorEnvelope } })?.response?.data;
    const code = data?.error?.code;
    if (code === 'NOT_FOUND') return null;
    throw e;
  }
}

/** Request for POST /v1/billing/checkout/tenant-subscription */
export interface CheckoutTenantRequest {
  plan_id: string;
  success_url: string;
  cancel_url: string;
  coupon_code?: string;
  idempotency_key?: string;
}

/** Response from POST /v1/billing/checkout/tenant-subscription */
export interface CheckoutTenantResponse {
  checkout_url: string;
  session_id: string;
}

/** POST /v1/billing/checkout/tenant-subscription — create Stripe Checkout Session for tenant subscription. Redirect user to checkout_url. */
export const createCheckoutSession = async (
  tenantId: string,
  body: CheckoutTenantRequest,
  idempotencyKey?: string
): Promise<CheckoutTenantResponse> => {
  const headers: Record<string, string> = { 'X-Tenant-Id': tenantId };
  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;
  const payload: CheckoutTenantRequest = {
    plan_id: body.plan_id,
    success_url: body.success_url,
    cancel_url: body.cancel_url,
    ...(body.coupon_code && { coupon_code: body.coupon_code }),
    ...(body.idempotency_key && { idempotency_key: body.idempotency_key }),
  };
  const response = await billingClient.post<CheckoutTenantResponse>(
    '/v1/billing/checkout/tenant-subscription',
    payload,
    { headers }
  );
  return response.data;
};

// ——— Coupons ———

export type Coupon = {
  coupon_id: string;
  code: string;
  type: 'percent' | 'amount';
  value: number;
  currency?: string | null;
  duration: 'once' | 'repeating' | 'forever';
  duration_in_months?: number | null;
  max_redemptions?: number | null;
  redeem_by?: number | null;
  status: 'active' | 'inactive';
  stripe_coupon_id?: string | null;
  stripe_promo_code_id?: string | null;
  created_at?: string;
};

export type CreateCouponRequest = {
  code: string;
  type: 'percent' | 'amount';
  value: number;
  duration: 'once' | 'repeating' | 'forever';
  duration_in_months?: number;
  currency?: string;
  max_redemptions?: number;
  redeem_by?: number;
};

export async function listCoupons(params?: {
  status?: 'active' | 'inactive';
  limit?: number;
  skip?: number;
}): Promise<Coupon[]> {
  const query: Record<string, string> = {};
  if (params?.status) query.status = params.status;
  if (typeof params?.limit === 'number') query.limit = String(params.limit);
  if (typeof params?.skip === 'number') query.skip = String(params.skip);
  const response = await billingClient.get<Coupon[]>('/v1/billing/coupons', { params: query });
  const data = response.data;
  return Array.isArray(data) ? data : [];
}

export async function createCoupon(body: CreateCouponRequest): Promise<Coupon> {
  const response = await billingClient.post<Coupon>('/v1/billing/coupons', body);
  return response.data;
}

export async function disableCoupon(couponId: string): Promise<Coupon> {
  const response = await billingClient.patch<Coupon>(`/v1/billing/coupons/${couponId}/disable`, {});
  return response.data;
}

/** Response from GET /v1/billing/coupons/validate?code=... (backend must implement this endpoint). */
export type CouponValidateResponse =
  | {
      valid: true;
      code: string;
      status: 'active';
      type: 'percent' | 'amount';
      value: number;
      currency?: string | null;
      duration: 'once' | 'repeating' | 'forever';
      duration_in_months?: number | null;
      max_redemptions?: number | null;
      redeem_by?: number | null;
      message?: string;
    }
  | {
      valid: false;
      code: string;
      message: string;
    };

/** GET /v1/billing/coupons/validate?code=CODE — validate coupon before checkout. */
export async function validateCouponCode(code: string): Promise<CouponValidateResponse> {
  const normalized = code.trim().toUpperCase();
  const response = await billingClient.get<CouponValidateResponse>('/v1/billing/coupons/validate', {
    params: { code: normalized },
  });
  return response.data;
}
